import { useCallback, useMemo } from 'react';

import { ChevronLeft, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DataTableEndlessPaginated } from '@/components/data-table/data-table-endless-paginated';
import { DataTableFilters } from '@/components/data-table/filters-v1/data-table-filters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDataTable } from '@/hooks/use-data-table';
import { useFilterValues } from '@/hooks/use-filter-values';
import { TableIds } from '@/lib/constants/table-ids';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { ErrorState } from '@/modules/common/components/organisms/error-state';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { usePermissions } from '@/modules/common/hooks/use-permissions';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { AuthenticatedRoutes } from '@/router/routes';

import { DeleteTeamMemberChangeModal } from '../../../components/modals/delete-team-member-change-modal';
import { getTeamMemberChangesTabFiltersConfig } from '../../../constants';
import { getTeamMemberChangesColumns } from '../../../constants/team-member-changes-table-columns';
import { useResolvedIntegrationChanges } from '../../../hooks/use-resolved-integration-changes';
import { useTeamMemberChangeDeletion } from '../../../hooks/use-team-member-change-deletion';
import { useTeamMemberChanges } from '../../../hooks/use-team-member-changes';
import { TeamMember, TeamMemberChangeLogEntry } from '../../../types';

interface TeamMemberChangesTabProps {
  featureData?: TeamMember;
  mode?: any;
  onFormStateChange?: (isDirty: boolean) => void;
}

export const TeamMemberChangesTab = ({
  featureData: teamMemberData,
}: TeamMemberChangesTabProps) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const isEditMemberEnabled = hasPermission(OpsiumPolicy.TeamMembersEdit);
  const filterConfig = getTeamMemberChangesTabFiltersConfig();
  const { values: appliedFilters } = useFilterValues(filterConfig);

  const teamMemberId = Number(teamMemberData?.id) || 0;

  const {
    data: changesData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useTeamMemberChanges({
    teamMemberId,
    filters: appliedFilters,
    enabled: !!teamMemberData?.id,
  });

  const resolvedChangesItems = useResolvedIntegrationChanges({
    integrations: teamMemberData?.integrations,
    changesItems: changesData?.items,
  });

  const {
    handleDeleteClick,
    handleSimpleDeleteConfirm,
    handleCloseSimpleDeleteConfirm,
    showSimpleDeleteConfirm,
    isSimpleDeletePending,
    handleDeleteSuccess,
    showDeleteModal,
    deleteConflictData,
    handleCloseDeleteModal,
  } = useTeamMemberChangeDeletion({
    changesItems: changesData?.items,
    teamMemberId,
  });

  const handleEditClick = useCallback(
    (row: TeamMemberChangeLogEntry) => {
      if (!teamMemberData?.id) return;
      navigate(`/team/${teamMemberData.id}/changes/${row.id}`, {
        replace: true,
        state: { directNavigation: true },
      });
    },
    [navigate, teamMemberData?.id]
  );

  const columns = useMemo(
    () =>
      getTeamMemberChangesColumns({
        handleEditClick,
        handleDeleteClick,
        isEditEnabled: isEditMemberEnabled,
      }),
    [handleEditClick, handleDeleteClick, isEditMemberEnabled]
  );

  const { table, saveColumnSizing } = useDataTable<TeamMemberChangeLogEntry>({
    data: resolvedChangesItems,
    columns,
    tableId: TableIds.TEAM_MEMBER_CHANGES,
    getRowId: row => `${row.id}`,
  });

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const getRowClassName = useCallback((row: TeamMemberChangeLogEntry) => {
    return row.isActive ? '!bg-success/5 hover:!bg-success/5' : '';
  }, []);

  if (error) {
    return (
      <ErrorState
        error={getErrorMessage(error)}
        className="min-h-[400px]"
        actions={[
          {
            label: t('common:retry'),
            onClick: () => refetch(),
            variant: 'default',
            icon: <RefreshCw className="h-4 w-4" />,
          },
          {
            label: t('common:back'),
            onClick: () => navigate(AuthenticatedRoutes.Team),
            variant: 'outline',
            icon: <ChevronLeft className="h-4 w-4" />,
          },
        ]}
      />
    );
  }

  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <CardHeader className="shrink-0">
        <CardTitle>{t('authenticated.team:details.tabs.changes.changeHistory')}</CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="shrink-0">
          <DataTableFilters filtersConfig={filterConfig} className="flex flex-wrap gap-2" />
        </div>

        <p className="shrink-0 text-xs text-muted-foreground">
          {changesData?.pagination?.total ?? 0}{' '}
          {t('authenticated.team:details.tabs.changes.changes').toLowerCase()}
        </p>

        <DataTableEndlessPaginated
          table={table}
          isLoading={isLoading || isFetchingNextPage}
          hasMore={hasNextPage || false}
          onLoadMore={handleLoadMore}
          getRowClassName={getRowClassName}
          stickyHeader
          tableId={TableIds.TEAM_MEMBER_CHANGES}
          onResizeEnd={saveColumnSizing}
        />
      </CardContent>

      <ConfirmationModal
        title={t('authenticated.team:details.tabs.changes.deleteModal.confirmTitle')}
        message={t('authenticated.team:details.tabs.changes.deleteModal.confirmMessage')}
        confirmText={t('authenticated.team:details.tabs.changes.deleteModal.delete')}
        confirmVariant="destructive"
        open={showSimpleDeleteConfirm}
        onClose={handleCloseSimpleDeleteConfirm}
        onConfirm={handleSimpleDeleteConfirm}
        isLoading={isSimpleDeletePending}
      />

      {deleteConflictData && (
        <DeleteTeamMemberChangeModal
          conflictData={deleteConflictData}
          teamMemberId={teamMemberId}
          onSuccess={handleDeleteSuccess}
          open={showDeleteModal}
          onClose={handleCloseDeleteModal}
        />
      )}
    </Card>
  );
};
